import React from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ItineraryStatusBadge } from "./ItineraryStatusBadge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    MoreHorizontal,
    Pencil,
    Eye,
    Copy,
    Trash2,
    FileText,
    Mail,
    Edit,
    Loader2,
} from "lucide-react";
import { cn, formatTravelDates } from "@/components/utils/utils";
import { getAvatarColor, getInitials } from "@/components/utils/avatar-colors";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { ItineraryStatus } from "@/lib/builder/models/itinerary.model";
import { useLanguage } from "@/contexts/LanguageContext";
import { useModal } from "@/contexts/ModalContext";
import { useItineraryPDF } from "@/hooks/useItineraryPDF";
import { toast } from "@/hooks/useToast";
import { getStorageThumbnailUrl } from "@/utils/imageStorage";
import { useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { SUPABASE_KEY, SUPABASE_URL } from "@/components/utils/constants";

interface ItinerariesTableProps {
    itineraries: DashboardItinerary[];
    currentPage: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    onEdit?: (itineraryId: string) => void;
    onDelete?: (itineraryId: string) => void;
    onDuplicate?: (itineraryId: string) => void;
    onViewTravelRequest?: (travelRequestId: string) => void;
    onViewItinerary?: (itineraryId: string) => void;
    onUpdateStatus?: (itineraryId: string, status: ItineraryStatus) => void;
    isAdmin?: boolean;
    isTraveler?: boolean;
    userRole?: string;
}

const ItineraryRowActions: React.FC<{
    itinerary: DashboardItinerary;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onDuplicate?: (id: string) => void;
    onViewTravelRequest?: (id: string) => void;
    onViewItinerary?: (id: string) => void;
    onUpdateStatus?: (id: string, status: ItineraryStatus) => void;
    isAdmin?: boolean;
    isTraveler?: boolean;
}> = ({ itinerary, onEdit, onDelete, onDuplicate, onViewTravelRequest, onViewItinerary, onUpdateStatus, isAdmin, isTraveler }) => {
    const { t } = useLanguage();
    const { generatePDF } = useItineraryPDF();
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const { openModal } = useModal();

    const handleSendItineraryInReview = async (): Promise<boolean> => {
        try {
            if (onUpdateStatus) {
                await onUpdateStatus(itinerary.id, 'pending');
                toast({ title: t('itinerary.requestSent'), description: t('itinerary.requestSentDesc') });
                return true;
            }
            return false;
        } catch {
            toast({ title: t('errors.generic'), description: t('itinerary.sendRequestError'), variant: "destructive" });
            return false;
        }
    };

    const handleSendQuote = async (stripeUrl: string, payment: string) => {
        try {
            setIsGeneratingPDF(true);

            // let pdfBase64: string | null = null;
            // let pdfFilename: string | null = null;

            // if (process.env.NODE_ENV !== 'development') {
            //     const pdfResult = await generatePDF(itinerary.id, false);
            //     pdfBase64 = pdfResult.pdf.output('datauristring').split(',')[1];
            //     if (!pdfBase64 || !pdfResult?.fileName) {
            //         toast({ title: t('errors.generic'), description: t('itinerary.pdfConversionError'), variant: "destructive" });
            //         setIsGeneratingPDF(false);
            //         return false;
            //     }
            //     pdfFilename = pdfResult.fileName;
            // }

            const supabaseClient = createClient(SUPABASE_URL, SUPABASE_KEY);
            const { error } = await supabaseClient.functions.invoke("send-quote", {
                body: JSON.stringify({
                    draftId: itinerary.id,
                    // pdfBase64,
                    // pdfFilename,
                    to: itinerary.contactEmail,
                    subject: `Your Travel Quote Is Ready`,
                    variables: {
                        client_first_name: itinerary?.travelRequest.contact.firstName,
                        client_last_name: itinerary?.travelRequest.contact.lastName,
                        itinerary_title: itinerary.title,
                        start_date: itinerary.startDate,
                        end_date: itinerary.endDate,
                        payment,
                        stripeUrl,
                    },
                }),
            });
            setIsGeneratingPDF(false);
            if (error) {
                toast({ title: t('itinerary.sendQuoteError'), description: t('itinerary.sendQuoteErrorDesc'), variant: "destructive" });
                return false;
            }
            toast({ title: t('itinerary.quoteSent'), description: t('itinerary.quoteSentDesc') });
            if (onUpdateStatus) await onUpdateStatus(itinerary.id, 'sended');
            return true;
        } catch {
            setIsGeneratingPDF(false);
            toast({ title: t('errors.generic'), description: t('itinerary.sendQuoteErrorGeneric'), variant: "destructive" });
            return false;
        }
    };

    const handleGeneratePDF = async () => {
        if (isGeneratingPDF) return;
        setIsGeneratingPDF(true);
        try {
            await generatePDF(itinerary.id, true);
        } catch {
            toast({ title: t('errors.generic'), description: t('itinerary.pdfGenerationError'), variant: "destructive" });
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                {isTraveler ? (
                    <DropdownMenuItem onClick={() => onViewItinerary?.(itinerary.id)}>
                        <Eye className="h-4 w-4 mr-2" />
                        {t('itinerary.viewItinerary')}
                    </DropdownMenuItem>
                ) : (
                    <>
                        <DropdownMenuItem onClick={() => onViewItinerary?.(itinerary.id)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            {t('itinerary.editItinerary')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onViewTravelRequest?.(itinerary.travelRequestId)}>
                            <Eye className="h-4 w-4 mr-2" />
                            {t('travelRequest.viewTripRequest')}
                        </DropdownMenuItem>
                        {isAdmin && (
                            <DropdownMenuItem onClick={() => window.open(`https://www.backoffice.place2be.it/itineraries/edit/${itinerary.id}`, '_blank')}>
                                <Edit className="h-4 w-4 mr-2" />
                                {t('itinerary.openInBackoffice')}
                            </DropdownMenuItem>
                        )}
                        {itinerary.status === 'draft' && (
                            <DropdownMenuItem onClick={() => openModal('create-quote', { onSend: handleSendItineraryInReview })}>
                                <FileText className="h-4 w-4 mr-2" />
                                {t('itinerary.submitForApproval')}
                            </DropdownMenuItem>
                        )}
                    </>
                )}

                {!isTraveler && (
                    <>
                        <DropdownMenuSeparator />
                        {itinerary.status === 'approved' && (
                            <>
                                <DropdownMenuItem onClick={() => openModal('send-quote', { itinerary, onSendQuote: handleSendQuote })}>
                                    <Mail className="h-4 w-4 mr-2" />
                                    {t('itinerary.sendToClient')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                    </>
                )}

                <DropdownMenuItem onClick={handleGeneratePDF} disabled={isGeneratingPDF}>
                    {isGeneratingPDF ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                    {isGeneratingPDF ? t('itinerary.generatingPDF') : t('itinerary.generatePDF')}
                </DropdownMenuItem>

                {!isTraveler && itinerary.status !== 'sended' && itinerary.status !== 'completed' && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onDuplicate?.(itinerary.id)}>
                            <Copy className="h-4 w-4 mr-2" />
                            {t('common.duplicate')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onDelete?.(itinerary.id)}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('common.delete')}
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export const ItinerariesTable: React.FC<ItinerariesTableProps> = ({
    itineraries,
    currentPage,
    itemsPerPage,
    onPageChange,
    onEdit,
    onDelete,
    onDuplicate,
    onViewTravelRequest,
    onViewItinerary,
    onUpdateStatus,
    isAdmin,
    isTraveler,
    userRole,
}) => {
    const { t } = useLanguage();
    const { openModal } = useModal();

    const totalPages = Math.ceil(itineraries.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItineraries = itineraries.slice(startIndex, startIndex + itemsPerPage);

    const handleRowClick = (itinerary: DashboardItinerary) => {
        openModal('itinerary-details', {
            itinerary,
            onViewItinerary: (id: string) => onViewItinerary?.(id),
        });
    };

    return (
        <div className="space-y-4">
            <div className="overflow-x-auto">
                <div className="min-w-[900px]">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('itinerary.title')}</TableHead>
                                <TableHead>{t('itinerary.startDate')}</TableHead>
                                <TableHead>{t('itinerary.duration')}</TableHead>
                                <TableHead>{t('itinerary.status')}</TableHead>
                                <TableHead>{t('travelRequest.contacts')}</TableHead>
                                {isAdmin && <TableHead>{t('travelRequest.designer')}</TableHead>}
                                <TableHead>{t('common.actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedItineraries.map((itinerary) => (
                                <TableRow
                                    key={itinerary.id}
                                    className="cursor-pointer hover:bg-gray-50"
                                    onClick={() => handleRowClick(itinerary)}
                                >
                                    {/* Titolo con thumbnail */}
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-10 h-10 rounded-md bg-gray-200 bg-cover bg-center flex-shrink-0"
                                                style={{
                                                    backgroundImage: itinerary.image
                                                        ? `url(${getStorageThumbnailUrl(itinerary.image, { noTransform: true })})`
                                                        : undefined,
                                                }}
                                            />
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {itinerary.title || t('itinerary.untitledItinerary')}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {itinerary.travelRequest.destinations?.join(', ') || '—'}
                                                </div>
                                            </div>
                                        </div>
                                    </TableCell>

                                    {/* Date */}
                                    <TableCell className="text-sm text-gray-600">
                                        {formatTravelDates(itinerary.startDate, itinerary.endDate)}
                                    </TableCell>

                                    {/* Ospiti */}
                                    <TableCell className="text-sm text-gray-600">
                                        {itinerary.travelRequest.totalGuests ?? '—'} {itinerary.travelRequest.totalGuests === 1 ? t('itinerary.guest') : t('itinerary.guests')}
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell>
                                        <div className="flex items-center inline-flex">
                                            <ItineraryStatusBadge status={itinerary.status} userRole={userRole} />
                                        </div>
                                    </TableCell>

                                    {/* Contatto */}
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Avatar className="h-7 w-7">
                                                <AvatarFallback className={`text-xs text-white ${getAvatarColor(itinerary.contactEmail)}`}>
                                                    {getInitials(
                                                        itinerary.contactFullName.split(' ')[0],
                                                        itinerary.contactFullName.split(' ').at(-1)
                                                    )}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{itinerary.contactFullName}</div>
                                                <div className="text-xs text-gray-500">{itinerary.contactEmail}</div>
                                            </div>
                                        </div>
                                    </TableCell>

                                    {/* Designer (solo admin) */}
                                    {isAdmin && (
                                        <TableCell>
                                            {itinerary.assignedAdvisor ? (
                                                <div className="flex items-center gap-2">
                                                    <Avatar className="h-7 w-7">
                                                        <AvatarFallback className={`text-xs text-white ${getAvatarColor(itinerary.assignedAdvisor.email)}`}>
                                                            {getInitials(itinerary.assignedAdvisor.firstName, itinerary.assignedAdvisor.lastName)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <span className="text-sm text-gray-900">
                                                        {itinerary.assignedAdvisor.firstName} {itinerary.assignedAdvisor.lastName}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">{t('travelRequest.notAssigned')}</span>
                                            )}
                                        </TableCell>
                                    )}

                                    {/* Azioni */}
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <ItineraryRowActions
                                            itinerary={itinerary}
                                            onEdit={onEdit}
                                            onDelete={onDelete}
                                            onDuplicate={onDuplicate}
                                            onViewTravelRequest={onViewTravelRequest}
                                            onViewItinerary={onViewItinerary}
                                            onUpdateStatus={onUpdateStatus}
                                            isAdmin={isAdmin}
                                            isTraveler={isTraveler}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Paginatore */}
            {totalPages > 1 && (
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        {t('dashboard.showingResults', {
                            from: startIndex + 1,
                            to: Math.min(startIndex + itemsPerPage, itineraries.length),
                            total: itineraries.length,
                        })}
                    </div>
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => onPageChange(currentPage - 1)}
                                    className={cn("cursor-pointer", currentPage === 1 && "pointer-events-none opacity-50")}
                                />
                            </PaginationItem>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                    return (
                                        <PaginationItem key={page}>
                                            <PaginationLink
                                                onClick={() => onPageChange(page)}
                                                isActive={currentPage === page}
                                                className="cursor-pointer"
                                            >
                                                {page}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                    return (
                                        <PaginationItem key={page}>
                                            <PaginationEllipsis />
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            <PaginationItem>
                                <PaginationNext
                                    onClick={() => onPageChange(currentPage + 1)}
                                    className={cn("cursor-pointer", currentPage === totalPages && "pointer-events-none opacity-50")}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    );
};
